// import { Pagination } from "react-bootstrap";
import CachedIcon from "mdi-react/CachedIcon";
import { useQuery } from "react-query";
import currency from "currency.js";
import { format, parse } from "date-fns";
import { useEffect, useState, useRef } from "react";
import queryString from "query-string";
import { CSVLink } from "react-csv";

import "./../../assets/scss/reports/cashbook.scss";
import useDebounce, {
  useCurrencies,
  useQueryParams,
  useScrollTop,
} from "../../utils/hooks";
import { useAuth } from "../../hooks/useAuth";
import ModalLoader from "../utils/ModalLoader";
import RsDateRangePicker from "../utils/RsDateRangePicker";
import { ExcelIcon, PDFIcon, ExportIcon } from "../Icons";
import { useDownloadExcel } from "../../hooks/useDownloadExcel";
import { Link } from "react-router-dom";
import { IsPrivileged } from "../DisplayChildElement";
import { backendApis } from "../../config";
import { Dropdown, Form } from "react-bootstrap";
import { isNumber } from "lodash";

function IncomeStatementByJob({
  filterParams,
  setFilterParams,
  queryParams,
  setQueryParams,
  jobNumber = "",
  poNumber = "",
  hideZeroBalance = false,
}) {
  useScrollTop();

  const {
    deploymentCurrencies: currenciesOptions,
    prevailingCurrency,
    getCurrencySymbol,
  } = useCurrencies();
  const [disableDate, setDisableDate] = useState(null);
  const [excelData, setExcelData] = useState([]);
  const CSVLinkRef = useRef(null);

  // fetch excel hook
  const [isfetchingExcel, fetchExcelData] = useDownloadExcel(
    excelData,
    CSVLinkRef
  );
  const { backendUrl, brachData } = useAuth();

  const fetchCustomerLedgers = async (queryParams) => {
    let data = {};

    // IN PROGRESS: Consolidate data from multiple branches if consolidate is true
    if (filterParams.consolidate) {
      const allDatas = await Promise.all(
        backendApis.map((endPoints) => {
          return new Promise(async (resolve, reject) => {
            try {
              let response = await fetch(
                `${
                  endPoints.url
                }/api/reports/income-statement?&${queryString.stringify({
                  ...queryParams,
                  jobNumber,
                  poNumber,
                })}`,
                {
                  method: "GET",
                  headers: {
                    Accept: "Application/json",
                    "Content-Type": "Application/json",
                  },
                  credentials: "include",
                }
              );

              if (!response.ok) {
                response = await response.json();
                throw new Error(response.message);
              }

              const { data } = await response.json();
              resolve(data);
            } catch (error) {
              console.log(error);
              reject(error);
            }
          });
        })
      );

      data = {
        accountIdsForReport: {},
        administrativeExpenses: {},
      };

      for (let branchData of allDatas) {
        for (let [key, value] of Object.entries(branchData)) {
          if (key == "accountIdsForReport") {
            for (let accId in value) {
              data[key][accId] = branchData[key]?.[accId];
            }
          } else if (key == "administrativeExpenses") {
            for (let accId in value) {
              data[key][accId] = currency(branchData[key]?.[accId]).add(
                data[key][accId]
              ).value;
            }
          } else {
            data[key] = isNumber(value)
              ? currency(data[key]).add(value).value
              : value;
          }
        }
      }
    } else {
      let response = await fetch(
        `${backendUrl}/api/reports/income-statement?&${queryString.stringify({
          ...queryParams,
          jobNumber,
          poNumber,
        })}`,
        {
          method: "GET",
          headers: {
            Accept: "Application/json",
            "Content-Type": "Application/json",
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        response = await response.json();
        throw new Error(response.message);
      }

      let responseData = await response.json();
      data = responseData.data;
    }

    // ===================================================
    data.grossProfit = currency(data.salesRevenue, {
      symbol: "",
    })
      .add(data.serviceFeeIncome)
      .add(data.otherIncome)
      .subtract(data.costOfgoodsold)
      .format();

    data.administrativeExpensesTotal = currency(
      Object.entries(data.administrativeExpenses).reduce(
        (a, c) => a + parseFloat(c[1]),
        0
      ),
      {
        symbol: "",
      }
    ).format();

    data.netIncome = currency(data.grossProfit, {
      symbol: "",
    })
      .subtract(data.administrativeExpensesTotal)
      .format();

    return data;
  };

  const {
    data = {
      count: 0,
      customerLedgers: [],
    },
    refetch,
    isFetching,
    // isSuccess,
  } = useQuery(
    ["INCOME_STATEMENT", queryParams],
    () => fetchCustomerLedgers(queryParams),
    {
      keepPreviousData: true,
    }
  );

  console.log(
    "this si the query params ==== ",
    queryString.stringify(queryParams)
  );

  // download excel function
  const { limit, page, ...rest } = queryParams;
  const onDownloadExcelData = async () => {
    // NO need to get from api, there is no pagination
    let exData = {
      data,
    };

    const {
      company,
      salesRevenue,
      otherIncome,
      costOfgoodsold,
      administrativeExpenses,
      serviceFeeIncome,
    } = exData.data;

    const grossProfit = currency(salesRevenue, {
      symbol: getCurrencySymbol(filterParams.convertCurrency),
    })
      .add(serviceFeeIncome)
      .add(otherIncome)
      .subtract(costOfgoodsold)
      .format();

    const administrativeExpensesTotal = currency(
      Object.entries(administrativeExpenses).reduce(
        (a, c) => a + parseFloat(c[1]),
        0
      ),
      {
        symbol: getCurrencySymbol(filterParams.convertCurrency),
      }
    ).format();

    const netIncome = currency(grossProfit, {
      symbol: getCurrencySymbol(filterParams.convertCurrency),
    })
      .subtract(administrativeExpensesTotal)
      .format();

    const admins = Object.entries(administrativeExpenses)
      .filter(([key, value]) => value > 0)
      .map((a, i) => [
        a[0],
        currency(a[1], {
          symbol: getCurrencySymbol(filterParams.convertCurrency),
        }).format(),
      ]);

    const date =
      rest.startDate && rest.endDate
        ? `Date Prepared: Between ${format(
            new Date(rest.startDate),
            "E MMM d yyyy k:mm:ss z"
          )} to ${format(new Date(rest.endDate), "E MMM d yyyy k:mm:ss z")}`
        : "";
    exData = [
      [company[0]?.CompName],
      ["Income Statement Report"],
      [date],
      [""],
      ["REVENUE"],
      [""],
      ...data.incomeJournal
        .filter((el) => el.RunningBalanceCalculated > 0)
        .map((el, i) => [
          el.DetailType,
          currency(el.RunningBalanceCalculated, {
            symbol: getCurrencySymbol(filterParams.convertCurrency),
          }).format(),
        ]),
      [
        "TOTAL REVENUE",
        currency(data.incomeJournalTotal, {
          symbol: getCurrencySymbol(filterParams.convertCurrency),
        }).format(),
      ],
      [""],
      ["COST OF SALES"],
      ...data.costOfSalesJournal
        .filter((el) => el.RunningBalanceCalculated > 0)
        .map((el, i) => [
          el.DetailType,
          currency(el.RunningBalanceCalculated, {
            symbol: getCurrencySymbol(filterParams.convertCurrency),
          }).format(),
        ]),
      [
        "TOTAL COST OF SALES",
        currency(data.costOfSalesJournalTotal, {
          symbol: getCurrencySymbol(filterParams.convertCurrency),
        }).format(),
      ],
      [""],
      ["Gross Profit", grossProfit],
      [""],
      [""],
      ["Administrative Expenses"],
      ...admins,
      [""],
      ["Total Expenses", administrativeExpensesTotal],
      [""],
      [""],
      ["Net Income", netIncome],
    ];
    //  console.log(exData);
    setExcelData(exData);
  };

  const filterByPeroid = (e) => {
    const selected = e.target;
    if (selected.value === "Date Range") {
      setDisableDate(false);
      setFilterParams({
        ...filterParams,
        month: "",
        startDate: format(new Date(), "yyyy-MM-dd"),
        endDate: format(new Date(), "yyyy-MM-dd"),
      });
    } else {
      setFilterParams({
        ...filterParams,
        month: selected.value,
        startDate: "",
        endDate: "",
      });
      setDisableDate(true);
    }
  };

  const getNavigationUrl = ({ el }) => {
    return null;
    const params = new URLSearchParams({
      currency: filterParams.currency || "",
      convertCurrency: filterParams.convertCurrency || "",
      startDate: filterParams.startDate || "",
      endDate: filterParams.endDate || "",
      year: filterParams.year || "",
      month: filterParams.month || "",
    });

    if (el?.Description) {
      params.set("AccountID", el.AccountID);
      params.set("Description", el.Description);
      return `/account-setup/account-list?${params.toString()}`;
    } else {
      params.set("Type", el.Type);
      params.set("DetailType", el.DetailType);
      params.set("subText", el?.subText || "");
      params.set("AccountID", el?.AccountID || "");
      params.set("jobNumber", jobNumber);
      params.set("poNumber", poNumber);
      return `/account-setup/chart-of-account-report?${params.toString()}`;
    }
  };

  const getNavigationUrlForExpenses = ({ a }) => {
    return null;
    const params = new URLSearchParams({
      AccountID: data?.accountIdsForReport?.[a[0]] || "",
      Description: a[0],
      currency: filterParams.currency || "",
      convertCurrency: filterParams.convertCurrency || "",
      startDate: filterParams.startDate || "",
      endDate: filterParams.endDate || "",
      year: filterParams.year || "",
      month: filterParams.month || "",
      jobNumber: jobNumber,
      poNumber: poNumber,
    });

    return `/account-setup/account-list?${params.toString()}`;
  };

  // isprivileged
  return (
    <IsPrivileged roleName="Financials">
      <main className="cash-book p-0">
        <div className="content pt-3">
          <div className="d-flex w-100 gap-3 justify-content-end px-3">
            <CSVLink
              className="btn print d-none"
              filename={`Project_Income_Summary_${jobNumber}_${poNumber}_${
                queryParams?.consolidate ? " Consolidated " : ""
              }(${format(new Date(), "dd-MMM-yyyy_hh_mm_ss_a")}).csv`}
              data={excelData}
              ref={CSVLinkRef}
            />
            {!queryParams?.consolidate && (
              <>
                <button
                  title="Refresh"
                  onClick={() => refetch()}
                  className="btn text-primary"
                >
                  <CachedIcon />
                </button>

                <Dropdown>
                  <Dropdown.Toggle
                    variant=""
                    className="btn print"
                    disabled={isfetchingExcel}
                    bsPrefix=""
                  >
                    <ExportIcon color="#008000" />
                    Export
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    popperConfig={{
                      strategy: "fixed",
                    }}
                    renderOnMount
                    className="text-center"
                  >
                    <Dropdown.Item onClick={onDownloadExcelData}>
                      Excel <ExcelIcon color="#008000" />
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </>
            )}
          </div>
          <div className="px-0 my-3">
            {/* REVENUE */}
            <div>
              <div className="fw-bold p-2 mb-2 bg-low-blue text-white ">
                <div className="container px-5">
                  <p className="">REVENUE</p>
                </div>
              </div>

              <div>
                <div className="container px-5">
                  {data?.incomeJournal &&
                    data.incomeJournal
                      .filter(
                        (el) =>
                          hideZeroBalance &&
                          Number(el.RunningBalanceCalculated) !== 0 // hideZeroBalance option
                      )
                      .map((el, i) => (
                        <div
                          className="d-flex justify-content-between align-items-center"
                          key={i}
                          to={getNavigationUrl({ el })}
                        >
                          <p className="fs-6 py-1">{el.DetailType}</p>
                          <p className="fs-6 py-1">
                            {currency(el.RunningBalanceCalculated, {
                              symbol: getCurrencySymbol(
                                filterParams.convertCurrency
                              ),
                            }).format()}
                          </p>
                        </div>
                      ))}
                </div>
              </div>

              <div className="fw-bold p-1 mt-0 bg-white border-top">
                <div className="d-flex justify-content-between align-items-center container px-5">
                  <p className="fs-6 my-2">TOTAL REVENUE</p>
                  <p className="fs-6 my-2">
                    {currency(data?.incomeJournalTotal, {
                      symbol: getCurrencySymbol(filterParams.convertCurrency),
                      precision: 2,
                    }).format()}
                  </p>
                </div>
              </div>
            </div>

            {/* COST OF SALES */}
            <div>
              <div className="fw-bold p-2 mb-2 mt-4 bg-low-blue text-white ">
                <div className="container px-5">
                  <p className="">COST OF SALES</p>
                </div>
              </div>

              <div>
                <div className="container px-5">
                  {data?.costOfSalesJournal &&
                    data.costOfSalesJournal
                      .filter(
                        (el) =>
                          hideZeroBalance &&
                          Number(el.RunningBalanceCalculated) !== 0 // hideZeroBalance option
                      )
                      .map((el, i) => (
                        <div
                          className="d-flex justify-content-between align-items-center"
                          key={i}
                          to={getNavigationUrl({ el })}
                        >
                          <p className="fs-6 py-1">{el.DetailType}</p>
                          <p className="fs-6 py-1">
                            {currency(el.RunningBalanceCalculated, {
                              symbol: getCurrencySymbol(
                                filterParams.convertCurrency
                              ),
                            }).format()}
                          </p>
                        </div>
                      ))}
                </div>
              </div>

              <div className="fw-bold p-1 mt-0 bg-white border-top">
                <div className="d-flex justify-content-between align-items-center container px-5">
                  <p className="fs-6 my-2">TOTAL COST OF SALES</p>
                  <p className="fs-6 my-2">
                    {currency(data?.costOfSalesJournalTotal, {
                      symbol: getCurrencySymbol(filterParams.convertCurrency),
                      precision: 2,
                    }).format()}
                  </p>
                </div>
              </div>
            </div>

            <div className="fw-bold p-1 mt-3 bg-primary text-white">
              <div className="d-flex justify-content-between align-items-center container px-5">
                <p className="fs-6 my-2">GROSS PROFIT</p>
                <p className="fs-6 my-2">
                  {currency(data?.grossProfit, {
                    symbol: getCurrencySymbol(filterParams.convertCurrency),
                    precision: 2,
                  }).format()}
                </p>
              </div>
            </div>

            {/* Administrative Expenses */}
            <div className="mt-5">
              <div className="fw-bold p-2 mb-2 bg-low-blue text-white ">
                <div className="container px-5">
                  <p>OPERATING EXPENSES (OE, AE)</p>
                </div>
              </div>
              <div>
                <div className="container px-5">
                  {data?.administrativeExpenses &&
                    Object.entries(data.administrativeExpenses)
                      .filter((a) => hideZeroBalance && Number(a[1]) !== 0) // hideZeroBalance option
                      .map((a, i) => (
                        <div
                          className="d-flex justify-content-between align-items-center"
                          key={i}
                        >
                          <p className="fs-6 py-1">
                            <div to={getNavigationUrlForExpenses({ a })}>
                              {a[0]}
                            </div>
                          </p>
                          <p className="fs-6 py-1">
                            <div to={getNavigationUrlForExpenses({ a })}>
                              {currency(a[1], {
                                symbol: getCurrencySymbol(
                                  filterParams.convertCurrency
                                ),
                              }).format()}{" "}
                            </div>
                          </p>
                        </div>
                      ))}
                </div>
              </div>
              <div className="fw-bold p-1 mt-0 bg-white border-top">
                <div className="d-flex justify-content-between align-items-center container px-5">
                  <p className="fs-6">Total Expenses</p>
                  <p className="fs-6 my-2">
                    {currency(data?.administrativeExpensesTotal, {
                      symbol: getCurrencySymbol(filterParams.convertCurrency),
                    }).format()}
                  </p>
                </div>
              </div>
            </div>

            <div className="fw-bold p-1 mt-0 bg-primary text-white">
              <div className="d-flex justify-content-between container px-5 align-items-center">
                <p className="fs-6">NET INCOME</p>
                <p className="fs-6 my-2">
                  {currency(data?.netIncome, {
                    symbol: getCurrencySymbol(filterParams.convertCurrency),
                  }).format()}
                </p>
              </div>
            </div>
          </div>
        </div>
        <ModalLoader
          show={isFetching || isfetchingExcel}
          {...(queryParams.consolidate ? { title: "Consolidating..." } : {})}
        />
      </main>
    </IsPrivileged>
  );
}

export default IncomeStatementByJob;
